import { ActionIcon, Alert, Avatar, Group, Image, Input, Stack, Text, Title, Tooltip } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useFocusTrap } from '@mantine/hooks';
import { IconAlertCircle, IconRefresh, IconX } from '@tabler/icons-react';
import cx from 'clsx';
import { CSSProperties, FormEvent, useMemo } from 'react';
import tinycolor from 'tinycolor2';
import { useTranslation } from 'react-i18next';
import {
  ChatContent,
  Markdown,
  TextDot,
  Thumb,
  ChatScroll,
  MESSAGE_SIGNIN_ICON_ARRAY,
  Loading,
  InputDisclaimer,
} from '@mygpt/app';
import DigitalHumanImg from '/digital-human-pic.png';
import { CHATBOT_AVATAR_ARRAY } from '@/components/Widget/Embed/Form/ChatbotAvatar';
import { I_IconSetting } from '@/components/Widget/Embed/form-context';
import { SvgSend } from '@mygpt/app/chat/components/icon';
import { useGPTStore } from '@/store';
import { SessionListItem, SessionListParam } from '@/types/interface';

import classes from './chatView.module.css';

export interface I_configs {
  suggestedMessages?: string[] | { id: string; question: string }[];
  welcomeMessages?: string[];
  signatureMessages?: {
    value: string;
    list: {
      iconIndex: number;
      value: string;
    }[];
  };
  inputPlaceholder?: string;
  securityNotice?: {
    checked: boolean;
  };
  footerBrand?: string;
  answerSource?: {
    checked: boolean;
  };
  showBotAvatar?: {
    checked: boolean;
  };
  widgetColor?: {
    chatColor?: string;
    sendButtonColor?: string;
    userMessageColor?: string;
    userMessageTextColor?: string;
    botMessageColor?: string;
    botMessageTextColor?: string;
  };
  chatHeaderImage?: string;
  chatbotAvatar: I_IconSetting;
  greetingMessage?: {
    checked: boolean;
    value: string;
  };
}

export interface FormValues {
  value: string;
}

interface Props {
  title: string;
  onRefresh: () => void;
  onClose: () => void;
  loading: boolean;
  sessionList: SessionListItem;
  historySessionList?: SessionListItem;
  submit: (values: { value: string; faqId?: string }, event?: FormEvent<HTMLFormElement>) => void;
  form: UseFormReturnType<FormValues>;

  closeAutoFocus?: boolean;
  configs?: I_configs;
  sessionInfo?: {
    aiId: string;
    sessionId: string;
  };
  updateSessionList?: (state: SessionListItem) => void;
}

export default (props: Props) => {
  const { t } = useTranslation();

  const {
    closeAutoFocus,
    configs,
    form,
    historySessionList,
    loading,
    onClose,
    onRefresh,
    sessionInfo,
    sessionList,
    submit,
    title,
    updateSessionList,
  } = props;
  const {
    answerSource,
    chatHeaderImage,
    chatbotAvatar,
    footerBrand,
    inputPlaceholder,
    securityNotice,
    signatureMessages,
    suggestedMessages,
    welcomeMessages,
    widgetColor,
    greetingMessage,
    showBotAvatar,
  } = configs ?? {};
  const { botMessageColor, chatColor, sendButtonColor, userMessageColor, userMessageTextColor } = widgetColor ?? {};

  const focusTrapRef = useFocusTrap();

  const { messages, pending } = sessionList;

  const chatMessages = useMemo(() => {
    const historySessionListMessage = historySessionList ? historySessionList.messages : [];
    return [
      ...historySessionListMessage,
      ...messages,
      ...(pending
        ? [
            {
              isAnswer: true,
              text: pending,
            },
          ]
        : []),
    ];
  }, [messages, pending, historySessionList]);

  const isLastMessage = (messageId: string) => {
    if (messages.length) {
      return messages.at(-1)?.messageId === messageId;
    } else if (historySessionList?.messages.length) {
      return historySessionList.messages.at(-1)?.messageId === messageId;
    } else {
      return false;
    }
  };

  const handleMessages = ({ messageId, rating }: { rating: number; messageId: string }) => {
    const index = messages.findIndex((item: SessionListParam) => item.messageId === messageId);
    messages[index] = { ...messages[index], ...{ rating } };
    updateSessionList && updateSessionList({ messages, pending });
  };

  const ChatbotAvatar = () => {
    const CustomAvatar = CHATBOT_AVATAR_ARRAY[chatbotAvatar?.index || 0];

    return (
      <Avatar size="sm" radius="xl" src={chatbotAvatar?.type === 'custom' ? chatbotAvatar.url : ''}>
        <span className={`${classes.avatar} ${classes.robotAvatar}`}>
          {/* <IconMessageChatbot size={20} /> */}
          <span className="scale-75">
            <CustomAvatar />
          </span>
        </span>
      </Avatar>
    );
  };

  return (
    <div className={`h-full flex flex-col`}>
      <section
        className={`${classes.header} flex-none p-5`}
        style={{
          background: chatHeaderImage
            ? `${chatColor || '#7C68FB'} url(${chatHeaderImage}) no-repeat center / contain`
            : chatColor || 'linear-gradient(90.07deg, #AF71FF 4.16%, #7C68FB 134.24%)',
        }}
      >
        <Group justify="space-between" wrap="nowrap">
          <Title style={{ wordBreak: 'break-word' }} order={5} fw={500} size={16}>
            {title}
          </Title>
          <Group wrap="nowrap">
            {!!chatMessages.length && (
              <Tooltip label={t('completions.reset_chat')}>
                <ActionIcon variant="subtle" onClick={onRefresh}>
                  <IconRefresh size={18} color="#FFF" />
                </ActionIcon>
              </Tooltip>
            )}
            <ActionIcon variant="subtle" onClick={onClose}>
              <IconX size={18} color="#FFF" />
            </ActionIcon>
          </Group>
        </Group>
      </section>
      <section className={`${classes.answerWrap} flex-grow overflow-y-auto`}>
        {chatMessages.map((item, index) => {
          const questionMessageColor = userMessageColor ? userMessageColor : '#F1EEFF';
          const answerMessageColor = botMessageColor ? botMessageColor : '#F9FAFB';
          return (
            <div
              key={index}
              className={`${classes.sessionItem} ${
                item.isAnswer ? classes.answerText : classes.questionText
              } flex gap-3 break-all`}
            >
              {item.isAnswer && showBotAvatar?.checked && (
                <span>
                  <ChatbotAvatar />
                </span>
              )}

              <div className="min-w-0">
                <div
                  className={cx('w-fit', classes.chatContent, !item.isAnswer ? classes.questionWrap : '')}
                  style={{ backgroundColor: !item.isAnswer ? questionMessageColor : answerMessageColor }}
                >
                  <ChatContent
                    id={sessionInfo?.aiId || ''}
                    item={item}
                    index={index}
                    answerSource={answerSource?.checked !== false}
                    pending={pending}
                    chatMessages={chatMessages}
                    sessionId={sessionInfo?.sessionId || ''}
                    signatureMessages={signatureMessages}
                    fetchQuestion={() => {}}
                    isLastMessage={isLastMessage}
                    regenerate={() => {}}
                    handleMessages={handleMessages}
                    getReference={() => Promise.resolve([])}
                    pageSource="admin-preview"
                    suggestedMessages={suggestedMessages}
                    submitQuestion={submit}
                    initMessagesCount={0}
                    widgetColor={widgetColor}
                  />
                </div>
              </div>
            </div>
          );
        })}
        {loading && !pending && (
          <div className={`${classes.sessionItem} ${classes.answerText} flex items-start gap-3 break-all`}>
            {showBotAvatar?.checked && (
              <span>
                <ChatbotAvatar />
              </span>
            )}
            <div className="flex items-center whitespace-pre-wrap">
              <Loading />
            </div>
          </div>
        )}
      </section>
      <section className={`${classes.inputWrap} flex-none px-5`}>
        {securityNotice?.checked !== false && <InputDisclaimer layout="inline" />}
        <form onSubmit={form.onSubmit(submit)}>
          <Input
            ref={closeAutoFocus ? null : focusTrapRef}
            size="md"
            className={classes.input}
            placeholder={inputPlaceholder ? inputPlaceholder : t('completions.input.placeholder')}
            rightSection={
              <ActionIcon
                variant="subtle"
                mr={10}
                type="submit"
                radius="xl"
                size="lg"
                loading={loading}
                // sendButtonColor 存在时:图标用该色,并把 subtle 变体 hover 背景覆盖成该色浅色调
                // (默认 hover 会落到主题紫);未配置时保持 Mantine 默认行为
                style={
                  sendButtonColor
                    ? ({
                        color: sendButtonColor,
                        '--ai-hover': tinycolor(sendButtonColor).setAlpha(0.1).toRgbString(),
                      } as CSSProperties)
                    : undefined
                }
              >
                {!loading && <SvgSend />}
              </ActionIcon>
            }
            {...form.getInputProps('value')}
          />
        </form>
        <section className="text-gray-400 min-h-[18px]">
          <Text ta="center" size="xs" mt={5} mb={5}>
            {footerBrand ? (
              footerBrand
            ) : (
              <>
                Powered by{' '}
                <a
                  href={import.meta.env.VITE_SUPPORT_WEBSITE}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-300 underline"
                >
                  GBase
                </a>
              </>
            )}
          </Text>
        </section>
      </section>
    </div>
  );
};
